import { Alert, Divider, Stack } from '@mui/joy';
import { lowerFirst } from 'lodash';
import { useContext } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { type ContactFormValues } from './useContactForm';
import { type TAutocompleteOption } from '~/common/types';

import { CaseRole, Gender, Language } from '~/common/enums';
import { AppContext } from '@/shared/context/context';
import { trpc } from '@/config/trpc';

import useDuplicateCheck from '../useDuplicateCheck';
import useToggle from '@/shared/hooks/UseToggle';

import ConfirmModal from '@/shared/components/2.0/ConfirmModal';
import EmailAddressInput from '@/containers/Email/EmailAddressInput';
import FormikAddressForm from '@/shared/formik/FormikAddressForm';
import FormikAutocomplete from '@/shared/components/2.0/formik/FormikAutocomplete';
import FormikIbansInput from '@/shared/components/2.0/input/FormikIbansInput';
import FormikMultiPhoneNumbersInput from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbersInput';
import FormikTextField from '@/shared/components/2.0/formik/FormikTextField';
import { InlineSpinner } from '@/shared/icons/Icons';
import ModalFooter from '@/shared/components/2.0/forms/ModalFooter';

type Props = {
  disabled?: boolean;
  isSubmitting: boolean;
  minified?: boolean;
  onCancel: () => void;
};

const AddEditContactFormBody: React.FC<Props> = ({
  disabled = false,
  isSubmitting,
  minified = false,
  onCancel,
}) => {
  const { t } = useTranslation(['common', 'errors']);

  const confirmToggle = useToggle();

  const genderOptions = Object.values(Gender).map((gender) => ({
    label: t(lowerFirst(gender)),
    value: gender,
  })) as TAutocompleteOption<Gender>[];

  const languageOptions = Object.values(Language).map((language) => ({
    label: t(lowerFirst(language)),
    value: language,
  })) as TAutocompleteOption<Language>[];

  const caseRoleOptions = Object.values(CaseRole).map((caseRole) => ({
    label: t(lowerFirst(caseRole)),
    value: caseRole,
  })) as TAutocompleteOption<CaseRole>[];

  const { isValid, values, submitForm } = useFormikContext<ContactFormValues>();

  const [checkForDuplicates, duplicatesData] = useDuplicateCheck({ values });

  const { currentTeam } = useContext(AppContext);
  const allowCaseManagement = !!currentTeam?.allowCaseManagement;

  const { data: tags } = trpc.contact.tags.useQuery();

  return (
    <>
      {!!duplicatesData.alertInfoProps && (
        <Alert
          startDecorator={
            duplicatesData.isChecking ? <InlineSpinner /> : <duplicatesData.alertInfoProps.Icon />
          }
          color={duplicatesData.alertInfoProps.color}
        >
          <p>
            {duplicatesData.alertInfoProps.label} {t('clickHereToViewContact.click')}{' '}
            <a
              href={`/contact/${duplicatesData.alertInfoProps.contactId}`}
              rel="noreferrer"
              target="_blank"
            >
              {t('clickHereToViewContact.here')}
            </a>{' '}
            {t('clickHereToViewContact.toViewContact')}
          </p>
        </Alert>
      )}
      <Stack rowGap={0.5}>
        <div className={allowCaseManagement ? 'flex gap-4' : ''}>
          <div className={allowCaseManagement ? 'w-1/2' : ''}>
            <FormikAutocomplete
              name="contactTags"
              id="contactTags"
              label={t('tags')}
              multiple
              options={tags || []}
            />
          </div>
          {allowCaseManagement && (
            <div className="w-1/2">
              <FormikAutocomplete
                name="defaultCaseRole"
                id="defaultCaseRole"
                label={t('defaultRole')}
                options={caseRoleOptions || []}
              />
            </div>
          )}
        </div>
        <Stack direction="row" spacing={1}>
          <FormikTextField
            id="firstName"
            name="firstName"
            label={t('common:firstName')}
            fullWidth
            type="text"
            disabled={disabled}
            onBlur={() => checkForDuplicates()}
          />
          <FormikTextField
            label={t('common:lastName')}
            fullWidth
            id="lastName"
            name="lastName"
            type="text"
            required
            disabled={disabled}
            onBlur={() => checkForDuplicates()}
          />
        </Stack>
        {!minified && (
          <>
            <Stack direction="row" spacing={1}>
              <FormikAutocomplete
                name="gender"
                label={t('common:gender')}
                options={genderOptions}
              />
              <FormikAutocomplete
                name="language"
                id="language"
                label={t('common:language')}
                options={languageOptions}
              />
            </Stack>
            <Divider className="my-2" />
            <FormikIbansInput />
            <Divider className="my-2" />
            <EmailAddressInput inputOnBlur={checkForDuplicates} onRemove={checkForDuplicates} />
            <Divider className="mb-2 mt-5" />
            <FormikMultiPhoneNumbersInput />
            <FormikAddressForm disabled={disabled} />
          </>
        )}
      </Stack>

      <ModalFooter
        onSubmit={() => {
          if (!duplicatesData.reason) {
            submitForm();
            return;
          }

          confirmToggle.show();
        }}
        submitButtonType="button"
        disableSubmit={!isValid || duplicatesData.isChecking}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
      />

      {confirmToggle.value && (
        <ConfirmModal
          bodyText={`${duplicatesData.alertInfoProps?.label} ${t('modals.confirm.description')}`}
          onConfirm={submitForm}
          toggle={confirmToggle}
        />
      )}
    </>
  );
};

export default AddEditContactFormBody;
